import React, { useMemo } from 'react';
import { Menu, Button, Badge, Dropdown, List, Typography } from 'antd';
import { UserOutlined, BellOutlined, CheckOutlined, EllipsisOutlined, PoweroffOutlined, SettingOutlined, ClusterOutlined, AppstoreOutlined } from '@ant-design/icons';
import './Navbar.css';
import RouteMap from './RouteMap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../hooks/use_subscription';
import { EntityImage } from '../components/Entities/EntityImage';
import { useNotifications } from '../hooks/use_notifications';
import { useAuth } from '../context/AuthContext';
import { useNodeInfo } from '../hooks/use_node_info';
import { useNodeApps } from '../hooks/use_node_apps';
import { useGatheringStatus } from '../hooks/use_gathering';
import NodeSwitcher from '../components/Node/NodeSwitcher';
import { useSuperAdmins } from '../hooks/use_super_admins';

function NavBar() {
    const { is_subscriber } = useSubscription();
    const { unreadCount, unreadMessagesTotal, notifications, dropdownOpen, openDropdown, closeDropdown, markAllRead, featureNotifications } = useNotifications();
    const { userPersonEntity } = useAuth();
    const { nodeId, nodeName, featureMessaging, featureHangoutSchedule, featureGathering } = useNodeInfo();
    // 'waiting' or 'open' means members have somewhere to be right now —
    // worth a "live" badge on the nav entry even before they click in.
    const gatheringStatus = useGatheringStatus({ enabled: featureGathering });
    const gatheringLive = gatheringStatus === 'waiting' || gatheringStatus === 'open';
    const { isServerAdmin } = useSuperAdmins();
    // Apps a superadmin installed in the node itself (node settings → Apps):
    // for everyone signed in here, so each gets a nav entry.
    const { apps: nodeApps } = useNodeApps();
    const navigate = useNavigate();
    const location = useLocation();

    const selectedNavKey = useMemo(() => {
        const navPaths = RouteMap
            .filter(route => route.placeinNavBar && route.path !== '/logout')
            .map(route => route.path);
        const match = navPaths
            .filter(p => location.pathname === p || location.pathname.startsWith(p + '/'))
            .sort((a, b) => b.length - a.length)[0];
        return match ? [match] : [location.pathname];
    }, [location.pathname]);

    const navItems = useMemo(() => {
        return RouteMap
            .filter(route => route.placeinNavBar && route.path !== '/logout')
            // Tier 2 gate (#36): hide the Messages entry on nodes without messaging.
            .filter(route => featureMessaging || route.path !== '/messages')
            // Node feature toggle: hide the Schedule/calendar entry on nodes
            // that have hangout scheduling off (feature_hangout_schedule).
            .filter(route => featureHangoutSchedule || route.path !== '/calendar')
            // Node feature toggle: hide Gathering on nodes without it
            // (feature_gathering).
            .filter(route => featureGathering || route.path !== '/gathering')
            .map(route => {
                let icon = route.icon;
                let label = route.name;
                if (route.path === '/browse') {
                    // Surfaces this federation node's id when known, falling back
                    // to the route's static name otherwise.
                    label = nodeId || route.name;
                }
                if (route.path === '/messages') {
                    // Badge lives on the icon (not the label) so the unread count
                    // still shows up on mobile, where the label text is hidden.
                    icon = (
                        <Badge count={unreadMessagesTotal} size="small" offset={[4, -2]}>
                            {icon}
                        </Badge>
                    );
                }
                if (route.path === '/gathering' && gatheringLive) {
                    // A plain dot, not a count — there's nothing to count,
                    // just "there's somewhere to be right now".
                    icon = (
                        <Badge dot color="var(--status-success)" offset={[4, -2]}>
                            {icon}
                        </Badge>
                    );
                }
                return { label, key: route.path, icon };
            });
    }, [unreadMessagesTotal, featureMessaging, featureHangoutSchedule, featureGathering, gatheringLive, nodeId]);
    // One entry per app installed in the node, after the built-in pages. Its
    // key is the app's route, which selectedNavKey falls back to, so the entry
    // highlights while that app is open.
    const nodeAppItems = useMemo(
        () => nodeApps.map(app => ({ label: app.name, key: `/app/${app.id}`, icon: <AppstoreOutlined /> })),
        [nodeApps],
    );
    // Gathering sits apart, on the right just before "me"; the rest run from the logo.
    const mainNavItems = [...navItems.filter(item => item.key !== '/gathering'), ...nodeAppItems];
    const endNavItems = navItems.filter(item => item.key === '/gathering');

    const moreItems = [
        {
            key: '/account',
            label: 'Account',
            icon: <SettingOutlined />,
            onClick: () => navigate('/account'),
        },
        ...(isServerAdmin ? [{
            key: '/node-account',
            // Same wording as the page's title: "Warmdata settings".
            label: nodeName ? `${nodeName.charAt(0).toUpperCase()}${nodeName.slice(1)} settings` : 'Node settings',
            icon: <ClusterOutlined />,
            onClick: () => navigate('/node-account'),
        }] : []),
        { type: 'divider' },
        {
            key: '/logout',
            label: 'Logout',
            icon: <PoweroffOutlined />,
            danger: true,
            onClick: () => navigate('/logout'),
        },
    ];

    const notificationDropdown = (
        <div className="notif-panel">
            <div className="notif-panel-header">
                <Typography.Text strong style={{ fontSize: 13 }}>Notifications</Typography.Text>
                {unreadCount > 0 && (
                    <Button type="text" size="small" icon={<CheckOutlined />} onClick={markAllRead} style={{ fontSize: 11 }}>
                        Mark all read
                    </Button>
                )}
            </div>
            <List
                style={{ maxHeight: 360, overflowY: 'auto' }}
                dataSource={notifications}
                locale={{ emptyText: 'No notifications' }}
                renderItem={item => (
                    // Not clickable for now. Every item used to navigate to
                    // /messages/<entity_id>, but kind spans join_request,
                    // user_registered, signup_pending and the registration
                    // decisions as well as message — and only message has a
                    // thread to land in. Restoring this means routing per
                    // kind, not putting the old blanket handler back.
                    <List.Item className={`notif-item${item.is_read ? '' : ' notif-item--unread'}`}>
                        <div style={{ width: '100%' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                                <Typography.Text strong style={{ fontSize: 12 }}>{item.sender_name || 'Someone'}</Typography.Text>
                                <Typography.Text type="secondary" style={{ fontSize: 10 }}>
                                    {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography.Text>
                            </div>
                            <Typography.Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block' }}>
                                {item.preview}
                            </Typography.Text>
                        </div>
                    </List.Item>
                )}
            />
        </div>
    );

    return (
        <header className="vellby-navbar">
            {/* The logo doubles as the node switcher for people who belong to
                several nodes (or may create one); otherwise it just goes home. */}
            <NodeSwitcher size={20} className="navbar-logo" onHome={() => navigate('/')} />
            <Menu
                id="tour-navbar"
                mode="horizontal"
                selectedKeys={selectedNavKey}
                onClick={(e) => navigate(e.key)}
                className="navbar-menu"
                style={{ background: 'transparent', borderBottom: 'none', flex: 1 }}
                items={mainNavItems}
                disabledOverflow={true}
            />
            {endNavItems.length > 0 && (
                <Menu
                    mode="horizontal"
                    selectedKeys={selectedNavKey}
                    onClick={(e) => navigate(e.key)}
                    className="navbar-menu"
                    style={{ background: 'transparent', borderBottom: 'none' }}
                    items={endNavItems}
                    disabledOverflow={true}
                />
            )}
            <div className="navbar-actions">
                {/* The person's own picture stands in for "me": uploaded or
                    generated, same as everywhere else. Subscribers keep their
                    gold mark as a ring around it rather than a tinted icon. */}
                <button
                    className={`navbar-icon-btn navbar-me-btn${userPersonEntity && location.pathname === `/entity/${userPersonEntity.id}` ? ' navbar-me-btn--active' : ''}${is_subscriber ? ' navbar-me-btn--subscriber' : ''}`}
                    title={userPersonEntity?.name || 'Me'}
                    aria-label={userPersonEntity?.name || 'Me'}
                    disabled={!userPersonEntity}
                    onClick={() => userPersonEntity && navigate(`/entity/${userPersonEntity.id}`)}
                >
                    {userPersonEntity
                        ? <EntityImage entity={userPersonEntity} size={22} className="navbar-me-avatar" />
                        : <UserOutlined />}
                </button>
                {featureNotifications && (
                    <Dropdown
                        open={dropdownOpen}
                        onOpenChange={(open) => { if (open) openDropdown(); else closeDropdown(); }}
                        dropdownRender={() => notificationDropdown}
                        trigger={['click']}
                        placement="bottomRight"
                    >
                        <button className="navbar-icon-btn">
                            <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                                <BellOutlined />
                            </Badge>
                        </button>
                    </Dropdown>
                )}
                <Dropdown menu={{ items: moreItems }} trigger={['click']} placement="bottomRight">
                    <button className="navbar-icon-btn">
                        <EllipsisOutlined style={{ transform: 'rotate(90deg)' }} />
                    </button>
                </Dropdown>
            </div>
        </header>
    );
}

export default NavBar;
